"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function ScrollAnimations() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const timer = setTimeout(() => {
      // Connect GSAP ScrollTrigger to Lenis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const lenis = (window as any).__lenis;

      if (lenis) {
        lenis.on("scroll", ScrollTrigger.update);

        gsap.ticker.add((time: number) => {
          lenis.raf(time * 1000);
        });

        gsap.ticker.lagSmoothing(0);
      }

      const ctx = gsap.context(() => {
        // === HERO ===
        gsap.to(".hero-content", {
          yPercent: -12,
          opacity: 0.2,
          ease: "none",
          scrollTrigger: {
            trigger: ".hero-section",
            start: "top top",
            end: "50% top",
            scrub: 1,
          },
        });

        // === DEPTH PARALLAX (prompt 5) ===
        gsap.utils
          .toArray<HTMLElement>("[data-parallax]")
          .forEach((el) => {
            const depth = parseFloat(el.dataset.parallax || "8");
            const trigger = el.closest("section") || el;
            gsap.fromTo(
              el,
              { yPercent: depth * 0.75 },
              {
                yPercent: depth * -0.75,
                ease: "none",
                scrollTrigger: {
                  trigger,
                  start: "top bottom",
                  end: "bottom top",
                  scrub: 1.2,
                },
              }
            );
          });

        // === COLLECTION CARDS ===
        gsap.utils.toArray<HTMLElement>(".collection-card").forEach((card, i) => {
          gsap.from(card, {
            y: 48,
            duration: 1.4,
            ease: "power3.out",
            delay: i * 0.12,
            scrollTrigger: {
              trigger: card,
              start: "top 82%",
              toggleActions: "play none none none",
            },
          });
        });

        // === RITUAL ROWS ===
        gsap.utils.toArray<HTMLElement>(".ritual-row").forEach((row, i) => {
          gsap.from(row, {
            y: 28,
            opacity: 0,
            duration: 0.9,
            ease: "power2.out",
            delay: i * 0.1,
            scrollTrigger: {
              trigger: row,
              start: "top 90%",
              toggleActions: "play none none none",
            },
          });
        });

        // === MANIFESTO STATEMENT ===
        gsap.from(".manifesto-statement", {
          y: 20,
          opacity: 0,
          filter: "blur(8px)",
          duration: 1.3,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".manifesto-statement",
            start: "top 82%",
            toggleActions: "play none none none",
          },
        });

        // === FOOTER STATEMENT ===
        gsap.from(".footer-statement", {
          y: 16,
          opacity: 0,
          duration: 1.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".footer-statement",
            start: "top 85%",
            toggleActions: "play none none none",
          },
        });

        // === SECTION DIVIDERS ===
        gsap.utils.toArray<HTMLElement>(".section-divider").forEach((el) => {
          gsap.from(el, {
            opacity: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 92%",
              toggleActions: "play none none none",
            },
          });
        });

        // === SCROLL PROGRESS ===
        gsap.to(".scroll-progress-line", {
          scaleX: 1,
          ease: "none",
          scrollTrigger: {
            trigger: document.body,
            start: "top top",
            end: "bottom bottom",
            scrub: 0.3,
          },
        });
      });

      return () => ctx.revert();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return null;
}